<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from 'vue'
  import { Transition } from 'vue'
  import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/vue/24/outline'

  const props = defineProps<{
    hasChanges: boolean
    onSave: () => void
  }>()

  const isVisible = ref(false)
  const isCollapsed = ref(false)
  let lastScrollY = 0

  const checkScroll = () => {
    const scrollY = window.scrollY
    isVisible.value = scrollY > 100 || (scrollY > 0 && scrollY < lastScrollY)
    lastScrollY = scrollY
  }

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  onMounted(() => {
    window.addEventListener('scroll', checkScroll)
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', checkScroll)
  })
</script>

<template>
  <Transition
    enter-active-class="transform transition-all duration-300 ease-out"
    enter-from-class="translate-y-10 opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transform transition-all duration-300 ease-in"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-10 opacity-0"
  >
    <div
      v-if="isVisible && hasChanges"
      class="fixed right-6 top-60 z-50 flex items-center justify-center"
    >
      <div
        class="flex items-center rounded-full bg-purple-600 shadow-lg transition-all duration-300 hover:shadow-xl"
        :class="{ 'py-3 pr-6': !isCollapsed, 'p-2': isCollapsed }"
      >
        <button
          class="rounded-full p-2 text-white hover:bg-purple-700 focus:outline-none"
          @click="toggleCollapse"
        >
          <component
            :is="isCollapsed ? ChevronLeftIcon : ChevronRightIcon"
            class="size-4"
          />
        </button>
        <Transition
          enter-active-class="transform transition-all duration-300 ease-out"
          enter-from-class="w-0 opacity-0"
          enter-to-class="w-auto opacity-100"
          leave-active-class="transform transition-all duration-300 ease-in"
          leave-from-class="w-auto opacity-100"
          leave-to-class="w-0 opacity-0"
        >
          <button
            v-if="!isCollapsed"
            class="ml-2 inline-flex items-center gap-x-2 text-sm font-semibold text-white focus:outline-none
              focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:pointer-events-none
              disabled:opacity-50"
            @click="onSave"
          >
            <svg
              class="size-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
              />
            </svg>
            Save Changes
          </button>
        </Transition>
      </div>
    </div>
  </Transition>
</template>
